import { isArray } from 'lodash';
import { Result } from '../interfaces/helpers';
import { RepRosterResult, ResultGeoServer } from '../interfaces/internal';
import { IK2ViewAddress } from '../models/K2ViewInteraction.model';
import { environment } from 'env/config';
import { IAddressExtension } from '../models';

export const itemsPerPage = environment.app.pageSize;

export type PossibleResult<T> =
  | T
  | T[]
  | Result<T>
  | RepRosterResult<T>
  | ResultGeoServer;

export enum TypeOfResult {
  object,
  array,
  sfdc,
  paginated,
  geoserver,
}

export enum SourceOfResult {
  d2cBE = 'd2cBE',
  reproster = 'reproster',
  geoserver = 'geoserver',
  internal = 'internal',
  d2cgeo = 'd2cgeo',
}

export const typeOfResult = <T>(result: PossibleResult<T>): TypeOfResult => {
  if ((result as Result<T>).items) {
    return TypeOfResult.paginated;
  } else if ((result as RepRosterResult<T>).records) {
    return TypeOfResult.sfdc;
  } else if ((result as ResultGeoServer).features) {
    return TypeOfResult.geoserver;
  } else if (isArray(result)) {
    return TypeOfResult.array;
  } else {
    return TypeOfResult.object;
  }
};

//TODO parse reproster query to get offset and limit and current page
export const mapRepRosterToPaginated = <I, T>(
  result: RepRosterResult<I>,
  createFromDTO: (data: I) => T
): Result<T> => ({
  items: result.records.map((item) => createFromDTO(item)),
  meta: {
    itemCount: result.records.length,
    itemsPerPage,
    totalItems: result.totalSize,
    totalPages: result.totalSize
      ? Math.ceil(result.records.length / result.totalSize)
      : 1,
    currentPage: 0,
  },
  links: {
    first: null,
    previous: null,
    next: result.nextRecordsUrl,
    last: null,
  },
  source: SourceOfResult.reproster,
});

export const mapArrayToPaginated = <I, T>(
  result: I[],
  createFromDTO: (data: I) => T,
  source?
): Result<T> => ({
  items: result.map((item) => createFromDTO(item)),
  meta: {
    itemCount: result.length,
    itemsPerPage,
    totalItems: result.length,
    totalPages: Math.ceil(result.length / itemsPerPage),
    currentPage: 0,
  },
  links: {
    first: null,
    previous: null,
    next: null,
    last: null,
  },
  source,
});

export const mapGeoServerToPaginated = <T>(
  result: ResultGeoServer,
  createFromDTO: (data) => T
): Result<T> => {
  const arrayToConvert = result.features ? result.features : [];
  const totalItems = result.numberMatched;

  return {
    items: arrayToConvert.map((item) => createFromDTO(item)),
    meta: {
      itemCount: result.numberReturned,
      itemsPerPage,
      totalItems,
      totalPages: Math.ceil(totalItems / itemsPerPage),
      currentPage: 0,
    },
    links: {
      first: null,
      previous: null,
      next:
        result.links && result.links.length !== 0
          ? result.links[0]?.href
          : null,
      last: null,
    },
    source: SourceOfResult.geoserver,
  };
};

export const mapToPaginatedInstance = <I, T>(
  result: Result<I>,
  createFromDTO: (data: I) => T
): Result<T> => ({
  ...result,
  meta: {
    itemCount: result.meta.itemCount,
    totalItems: result.meta.totalItems,
    itemsPerPage: parseInt(result.meta.itemsPerPage as any, 10),
    totalPages: result.meta.totalPages,
    currentPage: parseInt(result.meta.currentPage as any, 10) - 1,
  },
  items: result.items.map((item) => createFromDTO(item)),
  source: SourceOfResult.d2cBE,
});

export const mapResult = <I, T>(
  result: PossibleResult<I>,
  createFromDTO: (data: I) => T
): Result<T> => {
  const type = typeOfResult<I>(result);
  if (type === TypeOfResult.object) {
    return mapArrayToPaginated<I, T>(
      [result as any],
      createFromDTO,
      SourceOfResult.internal
    );
  } else if (type === TypeOfResult.geoserver) {
    return mapGeoServerToPaginated(result as any, createFromDTO);
  } else if (type === TypeOfResult.array) {
    return mapArrayToPaginated<I, T>(
      result as any,
      createFromDTO,
      SourceOfResult.internal
    );
  } else if (type === TypeOfResult.sfdc) {
    return mapRepRosterToPaginated<I, T>(
      result as RepRosterResult<I>,
      createFromDTO
    );
  } else if (type === TypeOfResult.paginated) {
    return mapToPaginatedInstance(result as Result<I>, createFromDTO);
  } else {
    throw new Error('Unexpected type of response');
  }
};

export const formatAddressName = (data: IK2ViewAddress): string => {
  let name = '';
  if (!data) {
    return name;
  }

  const {
    unit,
    streetNumberPrefix,
    streetNumber,
    streetNumberSuffix,
    dirPrefix,
    streetTypePrefix,
    streetName,
    streetTypeSuffix,
    dirSuffix,
    city,
    province,
    postalCode,
  } = data;

  // IF(!ISBLANK(Unit_c), Unit_c +' ', '') &
  name = !unit ? name : unit;

  // IF(!ISBLANK(Prefix_c), Prefix_c +' ', '') &
  name = !streetNumberPrefix ? name : `${name} ${streetNumberPrefix}`;

  // IF(!ISBLANK(Street_Number_c), Street_Number_c +' ', '') &
  name = !streetNumber ? name : `${name} ${streetNumber}`;

  // IF(!ISBLANK(Suffix_c), Suffix_c +' ', '') &
  name = !streetNumberSuffix ? name : `${name} ${streetNumberSuffix}`;

  // IF(!ISBLANK(Dirprefix_c), Dirprefix_c +' ', '') &
  name = !dirPrefix ? name : `${name} ${dirPrefix}`;

  // IF(!ISBLANK(Street_Type_Prefix_c), Street_Type_Prefix_c +' ', '') &
  name = !streetTypePrefix ? name : `${name} ${streetTypePrefix}`;

  // IF(!ISBLANK(Street_Name_c), Street_Name_c +' ', '') &
  name = !streetName ? name : `${name} ${streetName}`;

  // IF(!ISBLANK(Street_Type_c), Street_Type_c +' ', '') &
  name = !streetTypeSuffix ? name : `${name} ${streetTypeSuffix}`;

  // IF(!ISBLANK(Dirsuffix_c), Dirsuffix_c +' ', '') &
  name = !dirSuffix ? name : `${name} ${dirSuffix}`;

  // IF(!ISBLANK(City_c),City_c +' ', '') &
  name = !city ? name : `${name} ${city}`;

  // IF(!ISBLANK(TEXT(Province_c)),TEXT(Province_c) +' ', '') &
  name = !province ? name : `${name} ${province}`;

  // IF(!ISBLANK(Postal_Code_c), Postal_Code_c +' ', ''),
  name = !postalCode ? name : `${name} ${postalCode}`;

  return name;
};

export const formatStreetName = (data: IK2ViewAddress): string => {
  let name = '';
  if (!data) {
    return name;
  }

  const {
    dirPrefix,
    streetTypePrefix,
    streetName,
    streetTypeSuffix,
    dirSuffix,
  } = data;

  // IF(!ISBLANK(Dirprefix_c), Dirprefix_c +' ', '') &
  name = !dirPrefix ? name : dirPrefix;

  // IF(!ISBLANK(Street_Type_Prefix_c), Street_Type_Prefix_c +' ', '') &
  name = !streetTypePrefix ? name : `${name} ${streetTypePrefix}`;

  // IF(!ISBLANK(Street_Name_c), Street_Name_c +' ', '') &
  name = !streetName ? name : `${name} ${streetName}`;

  // IF(!ISBLANK(Street_Type_c), Street_Type_c +' ', '') &
  name = !streetTypeSuffix ? name : `${name} ${streetTypeSuffix}`;

  // IF(!ISBLANK(Dirsuffix_c), Dirsuffix_c +' ', '') &
  name = !dirSuffix ? name : `${name} ${dirSuffix}`;

  return name;
};

export const formatStreetNumber = (data: IK2ViewAddress): string => {
  let name = '';
  if (!data) {
    return name;
  }

  const { streetNumberPrefix, streetNumber, streetNumberSuffix } = data;

  // IF(!ISBLANK(Prefix_c), Prefix_c +' ', '') &
  name = !streetNumberPrefix ? name : streetNumberPrefix;

  // IF(!ISBLANK(Street_Number_c), Street_Number_c +' ', '') &
  name = !streetNumber ? name : `${name} ${streetNumber}`;

  // IF(!ISBLANK(Suffix_c), Suffix_c +' ', '') &
  name = !streetNumberSuffix ? name : `${name} ${streetNumberSuffix}`;

  return name;
};

export const capitalizeWord = (word: string): string =>
  `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

// Equibalant function of formatAddressName in IK2viewAddress
// Creating full address string from IAddressExtension
export const formatAddressString = (data: IAddressExtension): string => {
  let name = '';
  if (!data) {
    return name;
  }

  const {
    unit,
    streetNumberPrefix,
    streetNumber,
    streetNumberSuffix,
    dirPrefix,
    streetTypePrefix,
    streetName,
    streetTypeSuffix,
    dirSuffix,
    city,
    province,
    postalCode,
  } = data;

  /*
  // IF(!ISBLANK(Unit_c), Unit_c +' ', '') &
  name = !unit ? name : unit;

  // IF(!ISBLANK(Prefix_c), Prefix_c +' ', '') &
  name = !streetNumberPrefix ? name : `${name} ${streetNumberPrefix}`;

  // IF(!ISBLANK(Street_Number_c), Street_Number_c +' ', '') &
  name = !streetNumber ? name : `${name} ${streetNumber}`;

  // IF(!ISBLANK(Suffix_c), Suffix_c +' ', '') &
  name = !streetNumberSuffix ? name : `${name} ${streetNumberSuffix}`;

  // IF(!ISBLANK(Dirprefix_c), Dirprefix_c +' ', '') &
  name = !dirPrefix ? name : `${name} ${dirPrefix}`;

  // IF(!ISBLANK(Street_Type_Prefix_c), Street_Type_Prefix_c +' ', '') &
  name = !streetTypePrefix ? name : `${name} ${streetTypePrefix}`;

  // IF(!ISBLANK(Street_Name_c), Street_Name_c +' ', '') &
  name = !streetName ? name : `${name} ${streetName}`;

  // IF(!ISBLANK(Street_Type_c), Street_Type_c +' ', '') &
  name = !streetTypeSuffix ? name : `${name} ${streetTypeSuffix}`;

  // IF(!ISBLANK(Dirsuffix_c), Dirsuffix_c +' ', '') &
  name = !dirSuffix ? name : `${name} ${dirSuffix}`;

  // IF(!ISBLANK(City_c),City_c +' ', '') &
  name = !city ? name : `${name} ${city}`;

  // IF(!ISBLANK(TEXT(Province_c)),TEXT(Province_c) +' ', '') &
  name = !province ? name : `${name} ${province}`;

  // IF(!ISBLANK(Postal_Code_c), Postal_Code_c +' ', ''),
  name = !postalCode ? name : `${name} ${postalCode}`;

  return name;
  */

  // Updated string format copied from D2C1's formatAddressName
  name = !unit ? name : unit;
  name = !streetNumberPrefix ? name : `${name} ${streetNumberPrefix}`;
  name = !streetNumber ? name : `${name} ${streetNumber}`;
  name = !streetNumberSuffix ? name : `${name} ${streetNumberSuffix}`;
  name = !dirPrefix ? name : `${name} ${dirPrefix}`;
  name = !streetTypePrefix ? name : `${name} ${streetTypePrefix}`;
  name = !streetName ? name : `${name} ${streetName}`;
  name = !streetTypeSuffix ? name : `${name} ${streetTypeSuffix}`;
  name = !dirSuffix ? name : `${name} ${dirSuffix}`;
  name = !city ? name : `${name}, ${city}`;
  name = !province ? name : `${name}, ${province}`;
  name = !postalCode ? name : `${name}, ${postalCode}`;

  return name;
};
